import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { updateStatusConfirmingRequest } from 'lib/api/confirming';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import DialogTitle from 'lib/designSystem/DialogTitle';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { UpdateStatusTransitions } from '../..';

type MessageType = {
  warning: string;
  warningArray: string[];
  acceptance: string;
};

type MessagesHash = {
  [key: string]: {
    [key: string]: MessageType;
  };
};

interface UpdateStatusModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  requestId: number;
  setUp: (id?: number) => Promise<void>;
  updateStatusTransitions: UpdateStatusTransitions;
  oldStatus: string;
  selectedId: string;
}

const messagesHash: MessagesHash = {
  Aceptada: {
    IN_REVIEW: {
      warning: `Al actualizar el estado de la solicitud a 'En revisión', se eliminarán:`,
      warningArray: ['Las aprobaciones de riesgo de esta solicitud'],
      acceptance: 'Estoy seguro de actualizar el estado de la solicitud',
    },
  },
  'En giro': {
    ACCEPTED: {
      warning: `Al actualizar el estado de la solicitud a 'En giro', se eliminarán:`,
      warningArray: [
        'Las aprobaciones de operaciones de esta solicitud',
        'La nómina realizada',
      ],
      acceptance: 'Estoy seguro de actualizar el estado de la solicitud',
    },
    IN_REVIEW: {
      warning: `Al actualizar el estado de la solicitud a 'En revisión', se eliminarán:`,
      warningArray: [
        'Las aprobaciones de riesgo de esta solicitud',
        'La nómina realizada',
      ],
      acceptance: 'Estoy seguro de actualizar el estado de la solicitud',
    },
  },
};

const ConfirmingRequestUpdateStatusModal = (props: UpdateStatusModalProps) => {
  const {
    open,
    setOpen,
    requestId,
    setUp,
    updateStatusTransitions,
    oldStatus,
    selectedId,
  } = props;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const handleUpdateStatusConfirmingRequest = async () => {
    setLoading(true);
    try {
      await updateStatusConfirmingRequest(dispatch, requestId, {
        oldStatus: oldStatus,
        newStatus: newStatus,
      });
      showAlert(
        dispatch,
        'success',
        'La solicitud de confirming ha sido actualizada exitosamente.',
      );
      setOpen(false);
      if (selectedId) {
        await setUp(Number(selectedId));
      } else {
        await setUp();
      }
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al intentar actualizar la solicitud de confirming.',
      );
    }
    setLoading(false);
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogTitle setOpen={setOpen}>
        Actualizar estado de solicitud #{requestId}
      </DialogTitle>
      {loading ? (
        <DialogContent style={{ minHeight: 200 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <div>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography>Estado actual:</Typography>
                <Typography>
                  <b>{oldStatus}</b>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  style={{ marginBottom: '10px' }}
                  variant="outlined"
                >
                  <InputLabel>Nuevo estado</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => {
                      setNewStatus(e.target.value);
                      setAgreeTerms(false);
                    }}
                    fullWidth
                    label="Nuevo estado"
                  >
                    {updateStatusTransitions[oldStatus].map((status) => (
                      <MenuItem key={status.key} value={status.key}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography>
              {messagesHash[oldStatus]?.[newStatus]?.warning}
            </Typography>
            <div style={{ padding: 8 }}>
              {messagesHash[oldStatus]?.[newStatus]?.warningArray.map(
                (text, index) => (
                  <Typography key={index} variant="body1">
                    - {text}
                  </Typography>
                ),
              )}
            </div>

            {newStatus && (
              <div style={{ padding: '8px 0px 0px' }}>
                <FormControlLabel
                  hidden={!newStatus}
                  control={
                    <Checkbox
                      onChange={() => setAgreeTerms(!agreeTerms)}
                      value={agreeTerms}
                      checked={agreeTerms}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {messagesHash[oldStatus]?.[newStatus]?.acceptance}
                    </Typography>
                  }
                />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpen(false)}
              variant="outlined"
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={() => handleUpdateStatusConfirmingRequest()}
              variant="contained"
              color="primary"
              disabled={!agreeTerms}
            >
              Actualizar
            </CustomButton>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default ConfirmingRequestUpdateStatusModal;
